import { useEffect, useId, useRef, useState } from 'react';
import { resolveApiAsset, handleAdminAssetError } from '../config/urls';

const TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp' };
export const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp';

export const validateImageFile = (file) => {
  if (!file) return '';
  const extension = file.name.split('.').pop().toLowerCase();
  if (!TYPES[extension] || TYPES[extension] !== file.type) return 'Choose a JPEG, PNG, GIF, or WebP image.';
  if (file.size === 0) return 'The selected image is empty.';
  if (file.size > 5 * 1024 * 1024) return 'Choose an image that is 5 MB or smaller.';
  return '';
};

const ImageUploadField = ({ label = 'Image', currentImage, onImageChange, required = false, compact = false }) => {
  const id = useId();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const selectImage = (event) => {
    const selected = event.target.files[0] || null;
    const message = validateImageFile(selected);
    event.target.setCustomValidity(message);
    setError(message);
    setFile(message ? null : selected);
    onImageChange(message ? null : selected);
  };

  const clearSelection = () => {
    input.current.value = '';
    input.current.setCustomValidity('');
    setError('');
    setFile(null);
    onImageChange(null);
  };

  const source = preview || resolveApiAsset(currentImage);
  return (
    <div className={compact ? 'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3' : undefined}>
      {compact && source && <img src={source} onError={handleAdminAssetError} alt={file ? 'Selected image preview' : 'Current image'}
        className="row-span-5 h-24 w-16 rounded border border-gray-200 object-contain" />}
      <div className={compact ? 'col-start-2 min-w-0' : undefined}>
      <label htmlFor={id} className="block text-sm text-black mb-1">{label}{required ? ' *' : ''}</label>
      <input ref={input} id={id} type="file" accept={IMAGE_ACCEPT} required={required && !currentImage}
        onChange={selectImage} aria-invalid={Boolean(error)} aria-describedby={`${id}-help`} className="text-sm max-w-full" />
      <p id={`${id}-help`} className={`mt-1 text-xs ${error ? 'text-red-600' : 'text-gray-500'}`} role={error ? 'alert' : undefined}>
        {error || 'JPEG, PNG, GIF, or WebP. Maximum 5 MB.'}
      </p>
      {!compact && source && <img src={source} onError={handleAdminAssetError} alt={file ? 'Selected image preview' : 'Current image'}
        className="mt-2 max-h-36 max-w-full rounded border border-gray-200 object-contain" />}
      {file && <button type="button" onClick={clearSelection} className="mt-1 text-xs text-orange-700 underline">Clear selection</button>}
      {currentImage && !file && <p className="mt-1 text-xs text-gray-500">Current image will be kept unless you choose a replacement.</p>}
      </div>
    </div>
  );
};

export default ImageUploadField;
